import PropTypes from 'prop-types';
import { CityPropType, LatLongPropType } from 'common/prop-types';
import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import GrabMaps, { Marker, ZoomControl } from '@geo/grab-maps';
import usePrevious from 'common/usePrevious';
import { pushNotificationAction } from 'actions/app-actions';
import { connect } from 'react-redux';
import cx from 'classnames';
import { getMapTilesURL } from 'constants/app-constants';
import { getCityRefCoordinates } from 'helper/address-search-helpers';
import GeoDuck from 'ducks/geo-duck';
import { Button, Popover } from 'grab-ui';
import { getNumberFromStorage } from 'helper/storage';
import { injectUuidToPOI } from 'helper/geo-helper';
import I18n from 'i18n-js';
import NumberedPin from '../common/numbered-pin';
import styles from './styles.less';
import { NOTIFICATION_TYPE } from '../notification/notification';

const GrabMap = GrabMaps({
  dragRotate: false,
  // scrollZoom: false,
});

const dragMapOnboardingKey = '__onboarding-dragMap__';
const maxDragMapOnboarding = 1;

const MapWithDragSearch = ({
  className,
  city,
  latlng,
  displayPinIndex,
  showPinIndex,
  isSender,
  onMapSearchResult,
  dispatch,
}) => {
  const mapRef = useRef();
  const [zoomMag, setZoomMag] = useState(15);
  const [searching, setSearching] = useState(false);
  const [dragSearchLatLong, setDragSearchLatLong] = useState(undefined);
  const lastDragSearchLatLong = usePrevious(dragSearchLatLong);
  const [onboardingVisible, setOnboardingVisible] = useState(false);
  const lastLatLong = usePrevious(latlng);
  const movingFingerAnimationRef = useRef(null);

  // onboarding control
  useEffect(
    () => {
      setTimeout(() => {
        const showed = getNumberFromStorage(dragMapOnboardingKey);
        if (showed < maxDragMapOnboarding) {
          setOnboardingVisible(true);
          localStorage.setItem(dragMapOnboardingKey, showed + 1);
        }
      }, 1000);
    },
    [setOnboardingVisible],
  );

  useEffect(
    () => {
      if (
        onboardingVisible &&
        movingFingerAnimationRef &&
        movingFingerAnimationRef.current
      ) {
        Promise.all([
          import('lottie-web'),
          import('public/animations/drag-to-snap.json'),
        ]).then(([lottieModule, dragToSnapData]) => {
          lottieModule.default.loadAnimation({
            container: movingFingerAnimationRef.current,
            renderer: 'svg',
            loop: true,
            autoplay: true,
            animationData: dragToSnapData.default,
            name: 'dragToSnap',
          });
        });
      }
    },
    [onboardingVisible],
  );

  const closeOnboarding = useCallback(
    () => {
      setOnboardingVisible(false);
    },
    [setOnboardingVisible],
  );
  useEffect(
    () => {
      if (onboardingVisible && latlng && lastLatLong !== latlng) {
        closeOnboarding();
      }
    },
    [lastLatLong, latlng, onboardingVisible, closeOnboarding],
  );

  // map control
  const zoom = useCallback(
    diff => {
      const newZoomMag = zoomMag + diff;
      setZoomMag(newZoomMag);
    },
    [zoomMag, setZoomMag],
  );
  const onZoomControlClick = useCallback((_, diff) => zoom(diff), [zoom]);

  const onDragStart = useCallback(
    map => {
      setSearching(true);
    },
    [setSearching, setZoomMag],
  );
  const onDragEnd = useCallback(
    map => {
      if (map && map.getCenter) {
        const center = map.getCenter();
        if (center && center.lat && center.lng) {
          setDragSearchLatLong({
            latitude: center.lat,
            longitude: center.lng,
          });
        }
      }
    },
    [setSearching],
  );

  const onZoomEnd = useCallback(
    map => {
      if (map && map.getZoom) {
        const newZoomMag = map.getZoom();
        setZoomMag(newZoomMag);
      }
    },
    [setZoomMag],
  );

  const mapSearchCallback = useCallback(
    (response, error, searchParams) => {
      if (error) {
        dispatch(
          pushNotificationAction({
            id: 'errorMapSearch',
            type: NOTIFICATION_TYPE.WARNING,
            message: I18n.t('dragMapSearch.error.failedToReverseGeo'),
          }),
        );
        setSearching(false);
        return;
      }

      const { result, uuid } = response;
      injectUuidToPOI(result, uuid);
      onMapSearchResult(result, searchParams);
      setSearching(false);
    },
    [dispatch, setSearching, onMapSearchResult],
  );

  // monitor drag latlong
  useEffect(
    () => {
      if (dragSearchLatLong && lastDragSearchLatLong !== dragSearchLatLong) {
        const { latitude, longitude } = dragSearchLatLong;
        dispatch(
          GeoDuck.creators.mapSearch(
            latitude,
            longitude,
            isSender ? 'pickup' : 'dropoff',
            mapSearchCallback,
          ),
        );
      }
    },
    [dragSearchLatLong, lastDragSearchLatLong, mapSearchCallback, dispatch],
  );

  const { longitude = 0, latitude = 0 } = useMemo(
    () => {
      if (latlng) {
        return latlng;
      }
      return getCityRefCoordinates(city) || {};
    },
    [city, latlng],
  );

  useEffect(
    () => {
      if (
        mapRef &&
        mapRef.current &&
        mapRef.current.get$Map &&
        longitude &&
        latitude
      ) {
        const map = mapRef.current.get$Map();
        if (map && map.setCenter) {
          map.setCenter([longitude, latitude]);
        }
      }
    },
    [mapRef, longitude, latitude],
  );

  return (
    <div className={cx(styles.mapWithDragSearch, className)}>
      {onboardingVisible && (
        <div className={styles.onboardingMask} onClick={closeOnboarding}>
          <Popover
            visible
            placement={'topRight'}
            overlayClassName={styles.onboardingToolTip}
            content={
              <div className={styles.onboardingContent}>
                <div className={styles.title}>
                  {I18n.t('dragMapSearch.onboard.title')}
                </div>
                <div className={styles.body}>
                  {I18n.t('dragMapSearch.onboard.body')}
                </div>
                <Button
                  type={'primary'}
                  className={styles.button}
                  onClick={closeOnboarding}
                >
                  {I18n.t('dragMapSearch.onboard.button')}
                </Button>
              </div>
            }
          >
            <div className={styles.movingFinger}>
              <div
                className={styles.movingFingerAnimation}
                ref={movingFingerAnimationRef}
              />
            </div>
          </Popover>
        </div>
      )}
      {searching && (
        <NumberedPin
          className={styles.draggingPin}
          isSender={isSender}
          showNumber={showPinIndex}
          number={`${displayPinIndex}`}
          pointy
        />
      )}
      <GrabMap
        ref={mapRef}
        mapStyle={getMapTilesURL()}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        zoom={zoomMag}
        onZoomEnd={onZoomEnd}
        center={[longitude, latitude]}
      >
        {!searching && (
          <Marker coordinates={[longitude, latitude]} anchor="bottom">
            <NumberedPin
              isSender={isSender}
              showNumber={showPinIndex}
              number={`${displayPinIndex}`}
              pointy
            />
          </Marker>
        )}
        <ZoomControl
          className={styles.zoomControls}
          onClick={onZoomControlClick}
        />
      </GrabMap>
    </div>
  );
};

MapWithDragSearch.propTypes = {
  className: PropTypes.string,
  city: CityPropType.isRequired,
  latlng: LatLongPropType,
  displayPinIndex: PropTypes.number,
  showPinIndex: PropTypes.bool,
  isSender: PropTypes.bool,
  onMapSearchResult: PropTypes.func,
  dispatch: PropTypes.func.isRequired,
};

MapWithDragSearch.defaultProps = {
  className: undefined,
  displayPinIndex: 1,
  showPinIndex: false,
  isSender: false,
  latlng: undefined,
  onMapSearchResult: () => {},
};

export default connect()(MapWithDragSearch);
